import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useSignIn, useSignUp, useSignInWithGoogle } from '../../hooks/queries/useAuth';
import { useProjects } from '../../hooks/queries/useProjects';
import { AuthForm } from './AuthForm';
import { useAuthUIStore } from '../../store/authUIStore';
import { logger } from '../../utils/logger';

interface AuthModalProps {
  isOpen: boolean;
  mode: 'login' | 'signup';
  onClose: () => void;
  onModeChange: (mode: 'login' | 'signup') => void;
}

export function AuthModal({ isOpen, mode, onClose, onModeChange }: AuthModalProps) {
  const navigate = useNavigate();
  const { setAuthActiveTab } = useAuthUIStore();
  const [error, setError] = useState<string | null>(null);

  // Sync external props with the UI store
  useEffect(() => {
    if (mode) {
      setAuthActiveTab(mode);
    }
  }, [mode, setAuthActiveTab]);

  // Log component mount and props
  useEffect(() => {
    logger.component('AuthModal mounted/updated', {
      isOpen,
      mode,
    });
  }, [isOpen, mode]);

  const { 
    mutateAsync: signIn, 
    isPending: isSigningIn 
  } = useSignIn();
  
  const { 
    mutateAsync: signUp, 
    isPending: isSigningUp 
  } = useSignUp();
  
  const { 
    mutateAsync: googleSignIn, 
    isPending: isGoogleSigningIn 
  } = useSignInWithGoogle();
  
  const { refetch: fetchProjects } = useProjects();

  const loading = isSigningIn || isSigningUp || isGoogleSigningIn;

  const handleSubmit = async (email: string, password: string) => {
    logger.component('AuthModal handleSubmit called', {
      mode,
      email,
      passwordLength: password.length,
    });
    
    setError(null);

    try {
      if (mode === 'login') {
        logger.component('AuthModal attempting sign in', { email });
        // Pass as object instead of array to avoid overload confusion
        await signIn({ email, password });
        logger.component('AuthModal sign in successful', { email });
      } else {
        logger.component('AuthModal attempting sign up', { email });
        // Pass as object instead of array to avoid overload confusion
        await signUp({ email, password });
        logger.component('AuthModal sign up successful', { email });
      }

      logger.component('AuthModal fetching projects', { email });
      await fetchProjects();
      
      logger.component('AuthModal closing and navigating', { email });
      onClose();
      navigate('/project');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      logger.error('AuthModal authentication error', { error: err, message: errorMessage });
      setError(errorMessage);
    }
  };

  const handleGoogleSignIn = async () => {
    logger.component('AuthModal handleGoogleSignIn called');
    setError(null);

    try {
      await googleSignIn({});
      logger.component('Google sign-in initiated');
      // For Google sign-in, the OAuth flow handles navigation via callbacks
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      logger.error('Google sign-in error', { error: err, message: errorMessage });
      setError(errorMessage);
    }
  };

  const handleModeChange = (newMode: 'login' | 'signup') => {
    logger.component('AuthModal mode change', { from: mode, to: newMode });
    onModeChange(newMode);
    setError(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-xl w-full max-w-md border border-gray-700">
          <div className="flex items-center justify-between p-6 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>

          <AuthForm
            mode={mode}
            onSubmit={handleSubmit}
            onGoogleSignIn={handleGoogleSignIn}
            loading={loading}
            error={error}
          />

          <div className="text-center pb-6">
            <button
              type="button"
              onClick={() => handleModeChange(mode === 'login' ? 'signup' : 'login')}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              {mode === 'login'
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}